import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';

import {
	Button,
	Confirm,
	DynamicIconWrapper,
	Icons,
	Modal,
	Select,
	SpinnerOverlay,
	Tooltip,
} from '@components/common';
import ActionsMenu from '@components/common/ActionsMenu';
import MultiSearch from '@components/common/Table/MultiSearch';

import { useQueryPortalFilesMilestonesFilter } from '@hooks/portal/files/useQueryPortalFilesMilestonesFilter';
import { useMutatePortalFolders } from '@hooks/portal/folders/useMutatePortalFolders';
import { useOnClickOutside } from '@hooks/utils/useOnClickOutSide';

import { portalFolders as portalFoldersSelector } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { setMilestones, setSearchValue } from '@slices/portalFolders';

import { isEmpty } from '@utils/common';
import { setMultiSelectFilterUtil } from '@utils/filterUtil';

import { TSelectOption } from '@interfaces/components/componentTypes';
import { ITableSearch } from '@interfaces/components/table';
import { IDeleteFolderDTO, IRenameFolderDTO } from '@interfaces/portal/dto/foldersManagementDTO';
import { IPortalFolder } from '@interfaces/portal/folders';
import { IFolderActionDTO } from '../FolderItem/interfaces';
import { IFolderForMove } from '../MoveFolderForm/interfaces';

import { EMenuActionsTypes } from './enums';

import { EXPORT_MENU_MIN_WIDTH } from '@constants/common';
import { MODAL_WIDTH_480, MODAL_WIDTH_580 } from '@constants/modals';
import { EFolderActionsId } from '../FolderItem/constants';
import { getDeleteConfirmText, getExportMenuActions, searchTooltipText } from './constants';
import { resetFilterTooltipText } from '@components/common/Table/constants';

import FolderForm from '../FolderForm';
import FolderItem from '../FolderItem';
import MoveFolderForm from '../MoveFolderForm';

import styles from './FolderTree.module.scss';

interface IFolderTree {
	projectId: string;
	isLoading: boolean;
	folders: IPortalFolder[];
	selectedFolderId: string | null;
	selectFolder: (id: string) => void;
	isProjectActive: boolean;
	setPendingFolderId: (id: string | null) => void;
	isSingleFolder: boolean;
	isPortalAdmin: boolean;
}

const FolderTree = ({
	projectId,
	isLoading,
	folders,
	selectedFolderId,
	selectFolder,
	isProjectActive,
	setPendingFolderId,
	isSingleFolder,
	isPortalAdmin,
}: IFolderTree) => {
	const actionExportMenuWrapperRef = useRef<HTMLDivElement | null>(null);

	const dispatch = useAppDispatch();

	const { searchValue } = useAppSelector(portalFoldersSelector);

	const [isShowFilters, setIsShowFilters] = useState(false);

	const [expandedFolderId, setExpandedFolderId] = useState(selectedFolderId);

	const [isFolderFormModal, setIsFolderFormModal] = useState(false);
	const [parentFolderIdForCreate, setParentFolderIdForCreate] = useState<string | null>(null);
	const [isShowDeleteConfirm, setIsShowDeleteConfirm] = useState(false);
	const [folderForRemove, setFolderForRemove] = useState<IDeleteFolderDTO | null>(null);
	const [folderForRename, setFolderForRename] = useState<IRenameFolderDTO | null>(null);
	const [isEditMode, setIsEditMode] = useState(false);
	const [isMoveFolderModal, setIsMoveFolderModal] = useState(false);
	const [folderForMove, setFolderForMove] = useState<IFolderForMove | null>(null);
	const [isVisibleExportActionMenu, setIsVisibleExportActionMenu] = useState(false);
	const [selectedMilestones, setSelectedMilestones] = useState<TSelectOption<string>[] | null>(null);

	const toggleFilters = () => setIsShowFilters(prevState => !prevState);

	const openFolderForm = () => setIsFolderFormModal(true);

	const openDeleteConfirm = () => setIsShowDeleteConfirm(true);

	const closeFolderForm = () => {
		setParentFolderIdForCreate(null);

		setFolderForRename(null);

		setIsEditMode(false);

		setIsFolderFormModal(false);
	};

	const closeDeleteConfirm = () => {
		setFolderForRemove(null);

		setIsShowDeleteConfirm(false);
	};

	const closeMoveFolderModal = () => {
		setIsMoveFolderModal(false);
		setFolderForMove(null);
	};

	const toggleVisibleExportActionMenu = () => {
		if (!folders.length) {
			return;
		}

		setIsVisibleExportActionMenu(prevState => !prevState);
	};

	const onCreateFolderSuccess = useCallback((folderId: string) => {
		setPendingFolderId(folderId);
	}, [setPendingFolderId]);

	useOnClickOutside({ refs: [actionExportMenuWrapperRef], handler: () => setIsVisibleExportActionMenu(false) });

	const { portalFilesMilestonesFilter } = useQueryPortalFilesMilestonesFilter(projectId);

	const {
		createFolder,
		handleRenameFolder,
		handleCheckFolderName,
		handleDeleteFolder,
		handleMoveFolder,
		handleDownloadFolder,
		handleDownloadFolders,
		isLoadingDeleteFolder,
	} = useMutatePortalFolders({
		onCreateFolderSuccess,
		closeDeleteConfirm,
	});

	const onFolderActionClick = useCallback(({
		action,
		folderId,
		isParentFolder,
		folderName,
		folderHasContent,
		parentFolderId,
	}: IFolderActionDTO) => {
		switch (action) {
			case EFolderActionsId.add: {
				setParentFolderIdForCreate(folderId);

				openFolderForm();

				break;
			}

			case EFolderActionsId.delete: {
				setFolderForRemove({ folderId, isParentFolder });

				openDeleteConfirm();

				break;
			}

			case EFolderActionsId.rename: {
				setFolderForRename({ folderId, isParentFolder, folderName, parentFolderId });
				setIsEditMode(true);

				openFolderForm();

				break;
			}

			case EFolderActionsId.move: {
				setIsMoveFolderModal(true);

				setFolderForMove({
					folderId,
					folderName,
					folderHasContent,
					isParentFolder,
				});

				break;
			}

			case EFolderActionsId.download: {
				void handleDownloadFolder({ folderId });

				break;
			}
		}
	}, [handleDownloadFolder]);

	const deleteFolderHandler = () => {
		if (folderForRemove) {
			const { folderId, isParentFolder } = folderForRemove;

			void handleDeleteFolder({
				folderId,
				isParentFolder,
			});
		}
	};

	const onClickExportMenuItem = (actionId: string) => {
		if (actionId === EMenuActionsTypes.searchResults) {
			return handleDownloadFolders({
				projectId,
				searchValue,
			});
		}

		handleDownloadFolders({ projectId });
	};

	const exportMenuActions = getExportMenuActions({ isSearchResultsDisabled: !searchValue });

	const onSearchHandler = useCallback((searchValue: string) => dispatch(setSearchValue({ searchValue })), [dispatch]);

	const search: Array<ITableSearch> = useMemo(() => [
		{
			initialValue: searchValue,
			name: 'searchValue',
			handler: onSearchHandler,
			tooltipText: searchTooltipText,
			placeholder: searchTooltipText,
		},
	], [searchValue, onSearchHandler]);

	const deleteConfirmText = getDeleteConfirmText(folderForRemove?.isParentFolder);

	const isDisabledClearFilters = !(selectedMilestones && selectedMilestones.length);

	const onMilestonesFilterChange = (selectedOptions: TSelectOption<string>[]) => {
		setSelectedMilestones(selectedOptions);
	};

	const resetFilterHandler = () => {
		if (isDisabledClearFilters) return;

		setSelectedMilestones(null);

		toggleFilters();
	};

	useEffect(() => {
		setMultiSelectFilterUtil({
			selectedValues: selectedMilestones,
			setAction: setMilestones,
			fieldName: 'milestones',
			dispatch,
		});
	}, [dispatch, selectedMilestones]);

	return (
		<div className={styles.folderTreeContainer}>
			{isLoading && <SpinnerOverlay />}
			<div className={styles.folderTreeHeader}>
				<div className={styles.leftActions}>
					<MultiSearch
						isOpenSearch={false}
						search={search}
						searchThreshold={0}
						searchWidth='280px'
					/>
					<div
						className={styles.icon}
						onClick={toggleFilters}
					>
						<DynamicIconWrapper
							icon={Icons.filter}
							isActive={isShowFilters}
							isDirty={!isDisabledClearFilters && !isShowFilters}
						/>
					</div>
					<div
						ref={actionExportMenuWrapperRef}
						className={styles.exportAction}
						onClick={toggleVisibleExportActionMenu}
					>
						<div className={styles.icon}>
							<DynamicIconWrapper
								icon={Icons.export}
								isDisabled={!folders?.length}
							/>
						</div>
						<ActionsMenu
							actions={exportMenuActions}
							isVisibleMenu={isVisibleExportActionMenu}
							minWidth={EXPORT_MENU_MIN_WIDTH}
							onClick={onClickExportMenuItem}
						/>
					</div>
				</div>
				{isProjectActive
					&& <Button
						icon={Icons.plus()}
						onClick={openFolderForm}
						secondary
						text='Add folder'
						withoutBackground
					/>
				}
			</div>
			{isShowFilters &&
				<div className={styles.filters}>
					<Select
						handleChange={onMilestonesFilterChange}
						isClearable
						isFilterStyle
						isMulti
						isWithoutUseForms
						minWidth='250px'
						name='milestones'
						optionMaxWidth={200}
						options={portalFilesMilestonesFilter}
						placeholder='Milestone'
						value={selectedMilestones}
					/>
					<div
						className={cn(styles.resetBtn, { [styles.disabled]: isDisabledClearFilters })}
						onClick={resetFilterHandler}
					>
						<Tooltip content={resetFilterTooltipText}>
							<div>
								Clear filter
							</div>
						</Tooltip>
					</div>
				</div>
			}
			<div className={styles.separator} />
			<div className={styles.folderTree}>
				{isEmpty(folders) && !isLoading
					&& <div className={styles.emptyText}>
						Create folder to start uploading files
					</div>
				}
				{folders.map(folder => {
					return (
						<FolderItem
							key={folder.id}
							expandFolder={setExpandedFolderId}
							expandedFolderId={expandedFolderId}
							folder={folder}
							isPortalAdmin={isPortalAdmin}
							isProjectActive={isProjectActive}
							isSingleFolder={isSingleFolder}
							onFolderActionClick={onFolderActionClick}
							onSelect={selectFolder}
							selectedFolderId={selectedFolderId}
						/>
					);
				})}
			</div>
			<Modal
				isOpen={isFolderFormModal}
				onClose={closeFolderForm}
				title={isEditMode ? 'Rename folder' : 'Add folder'}
				width={MODAL_WIDTH_580}
			>
				<FolderForm
					closeFolderForm={closeFolderForm}
					createFolder={createFolder}
					folderForRename={folderForRename}
					handleCheckFolderName={handleCheckFolderName}
					isEditMode={isEditMode}
					parentFolderIdForCreate={parentFolderIdForCreate}
					projectId={projectId}
					renameFolder={handleRenameFolder}
				/>
			</Modal>
			<Modal
				isOpen={isMoveFolderModal}
				onClose={closeMoveFolderModal}
				title='Move folder'
				width={MODAL_WIDTH_580}
			>
				<MoveFolderForm
					closeMoveFolderForm={closeMoveFolderModal}
					folderForMove={folderForMove}
					handleMoveFolder={handleMoveFolder}
				/>
			</Modal>
			<Modal
				isOpen={isShowDeleteConfirm}
				onClose={closeDeleteConfirm}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					isLoading={isLoadingDeleteFolder}
					onClose={closeDeleteConfirm}
					onSubmit={deleteFolderHandler}
					question={deleteConfirmText.text}
				>
					{deleteConfirmText.description}
				</Confirm>
			</Modal>
		</div>
	);
};

export default memo(FolderTree);
